import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { generateAuditLogsExcelFile } from '@/lib/excel_gen/audit-logs'
import { useTRPC } from '@/trpc/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import { type } from 'arktype'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Filter,
  X,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { toast } from 'sonner'
import { auditLogColumns } from './-components/auditLogColumns'
import { ACTION_BADGE_VARIANT, AuditLogRow } from './-components/auditLogFormatters'
import { DataTableViewOptions } from './-components/DataTableViewOptions'

// Export pages through getAuditLogs at the DAL's max pageSize (200) rather than the
// table's own page size, and caps at 5 pages (1,000 rows) — large exports here are meant
// to be date-range/filter-bounded (same convention as other audit-log tools), not an
// unbounded "export everything ever" dump. Narrowing filters/date range is the escape
// hatch, surfaced via a toast if this cap is ever hit.
const EXPORT_PAGE_SIZE = 200
const MAX_EXPORT_PAGES = 5

// All known roles in the system
const ALL_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'hr_admin', label: 'HR Admin' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'employee', label: 'Employee' },
] as const

const ALL_ACTIONS = [
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
] as const

// All known resource types that can appear in audit logs. BusinessSettings is intentionally
// excluded — it's never instrumented (see 00_audit_logs.md/01_add_more_instrumentations.md),
// so filtering by it always returned zero rows with no explanation.
const RESOURCE_TYPES = [
  { value: 'Timesheet', label: 'Timesheet' },
  { value: 'EmployeeSettings', label: 'Employee Settings' },
  { value: 'Employee', label: 'Employee' },
  { value: 'EmployeeLoan', label: 'Employee Loan' },
] as const

export const Route = createFileRoute('/dashboard/admin/audit-logs/')({
  component: RouteComponent,
  validateSearch: type({
    page: 'number = 1',
    pageSize: 'number = 50',
    // All filters stored as strings in the URL
    actor: 'string = ""',
    roles: 'string = "admin,hr_admin,supervisor"',
    actions: 'string = ""',
    resourceType: 'string = ""',
    dateFrom: 'string = ""',
    dateTo: 'string = ""',
  }),
})

// Restores the fixed widths the table had before columns were extracted into auditLogColumns —
// tanstack-table doesn't apply widths on its own, this is purely a layout hint on TableHead.
const COLUMN_HEADER_CLASS: Partial<Record<string, string>> = {
  time: 'w-[160px]',
  role: 'w-[120px]',
  action: 'w-[80px]',
  resource: 'w-[140px]',
  actions: 'w-[140px] text-right',
}

// Parses a comma-separated URL param into an array
function parseCSV(param: string): string[] {
  if (!param.trim()) return []
  return param
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean)
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [isExporting, setIsExporting] = useState(false)

  const page = Math.max(1, Math.trunc(search.page))
  const pageSize = Math.min(Math.max(1, Math.trunc(search.pageSize)), 100)

  // Debounce actor text input to avoid firing on every keystroke
  const debouncedActor = useDebouncedValue(search.actor, 600)

  const selectedRoles = parseCSV(search.roles)
  const selectedActions = parseCSV(search.actions)
  const activeResourceType = search.resourceType || undefined
  const activeDateFrom = search.dateFrom || undefined
  const activeDateTo = search.dateTo || undefined

  // Expand dateTo to end-of-day so the range is fully inclusive
  const dateToEndOfDay = activeDateTo
    ? (DateTime.fromISO(activeDateTo, { zone: 'utc' }).endOf('day').toISO() ?? activeDateTo)
    : undefined

  const { data: result, isPending } = useQuery({
    ...api.auditLog.getAuditLogs.queryOptions({
      page,
      pageSize,
      ...(debouncedActor ? { actor: debouncedActor } : {}),
      ...(selectedRoles.length ? { roles: selectedRoles } : {}),
      ...(selectedActions.length ? { actions: selectedActions } : {}),
      ...(activeResourceType ? { resourceType: activeResourceType } : {}),
      ...(activeDateFrom ? { dateFrom: activeDateFrom } : {}),
      ...(dateToEndOfDay ? { dateTo: dateToEndOfDay } : {}),
    }),
  })

  const pagination = result?.pagination

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  const table = useReactTable({
    data: result?.data ?? [],
    columns: auditLogColumns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  })

  // Whether any filter is active (used for clear-all button)
  const hasActiveFilters =
    !!search.actor ||
    !!search.roles ||
    !!search.actions ||
    !!search.resourceType ||
    !!search.dateFrom ||
    !!search.dateTo

  function goToPage(nextPage: number): void {
    navigate({ search: (prev) => ({ ...prev, page: nextPage }) })
  }

  function goToPageSize(nextPageSize: number): void {
    navigate({ search: (prev) => ({ ...prev, page: 1, pageSize: nextPageSize }) })
  }

  function handleActorChange(value: string): void {
    navigate({ search: (prev) => ({ ...prev, page: 1, actor: value }) })
  }

  function handleRoleToggle(role: string): void {
    const current = parseCSV(search.roles)
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role]
    navigate({ search: (prev) => ({ ...prev, page: 1, roles: next.join(',') }) })
  }

  function handleActionToggle(action: string): void {
    const current = parseCSV(search.actions)
    const next = current.includes(action)
      ? current.filter((a) => a !== action)
      : [...current, action]
    navigate({ search: (prev) => ({ ...prev, page: 1, actions: next.join(',') }) })
  }

  function handleResourceTypeChange(value: string): void {
    // "all" means no filter
    navigate({
      search: (prev) => ({ ...prev, page: 1, resourceType: value === 'all' ? '' : value }),
    })
  }

  function handleDateFromChange(value: string): void {
    navigate({ search: (prev) => ({ ...prev, page: 1, dateFrom: value }) })
  }

  function handleDateToChange(value: string): void {
    navigate({ search: (prev) => ({ ...prev, page: 1, dateTo: value }) })
  }

  function clearAllFilters(): void {
    navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        actor: '',
        roles: '',
        actions: '',
        resourceType: '',
        dateFrom: '',
        dateTo: '',
      }),
    })
  }

  // Exports every row matching the current filters (not just the current page) — pages
  // through getAuditLogs at EXPORT_PAGE_SIZE, independent of the on-screen pageSize.
  async function handleExportToExcel(): Promise<void> {
    setIsExporting(true)
    const { ResultAsync } = await import('neverthrow')
    const allRows: AuditLogRow[] = []
    let currentPage = 1
    let truncated = false

    while (currentPage <= MAX_EXPORT_PAGES) {
      const result = await ResultAsync.fromPromise(
        queryClient.fetchQuery(
          api.auditLog.getAuditLogs.queryOptions({
            page: currentPage,
            pageSize: EXPORT_PAGE_SIZE,
            ...(debouncedActor ? { actor: debouncedActor } : {}),
            ...(selectedRoles.length ? { roles: selectedRoles } : {}),
            ...(selectedActions.length ? { actions: selectedActions } : {}),
            ...(activeResourceType ? { resourceType: activeResourceType } : {}),
            ...(activeDateFrom ? { dateFrom: activeDateFrom } : {}),
            ...(dateToEndOfDay ? { dateTo: dateToEndOfDay } : {}),
          }),
        ),
        (error) => error,
      )

      if (result.isErr()) {
        setIsExporting(false)
        toast.error('Failed to fetch audit logs for export')
        return
      }

      allRows.push(...result.value.data)

      if (!result.value.pagination.hasNextPage) break
      if (currentPage === MAX_EXPORT_PAGES) {
        truncated = true
        break
      }
      currentPage += 1
    }

    if (truncated) {
      toast.info(
        `Export capped at ${allRows.length} rows — narrow the filters or date range to export everything.`,
      )
    }

    await generateAuditLogsExcelFile(allRows)
    setIsExporting(false)
  }

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Audit Logs"
        text="A record of all administrative actions taken in your organization."
      />

      <div className="space-y-4">
        {/* Filter toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Actor search */}
          <div className="relative w-52">
            <Input
              placeholder="Search actor..."
              value={search.actor}
              onChange={(e) => handleActorChange(e.target.value)}
              className="h-8 pr-6"
            />
            {search.actor && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => handleActorChange('')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Roles multi-select popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1">
                <Filter className="h-3 w-3" />
                Roles
                {selectedRoles.length > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1 py-0 text-xs">
                    {selectedRoles.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="start">
              <div className="space-y-1">
                {ALL_ROLES.map((role) => {
                  const checked = selectedRoles.includes(role.value)
                  return (
                    <label
                      key={role.value}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleRoleToggle(role.value)}
                        className="h-3.5 w-3.5"
                      />
                      {role.label}
                    </label>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>

          {/* Action multi-select popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1">
                <Filter className="h-3 w-3" />
                Action
                {selectedActions.length > 0 && (
                  <Badge variant="secondary" className="ml-1 px-1 py-0 text-xs">
                    {selectedActions.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-2" align="start">
              <div className="space-y-1">
                {ALL_ACTIONS.map((action) => {
                  const checked = selectedActions.includes(action.value)
                  return (
                    <label
                      key={action.value}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleActionToggle(action.value)}
                        className="h-3.5 w-3.5"
                      />
                      {action.label}
                    </label>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>

          {/* Resource type select */}
          <Select value={search.resourceType || 'all'} onValueChange={handleResourceTypeChange}>
            <SelectTrigger className="h-8 w-44">
              <SelectValue placeholder="Resource type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              {RESOURCE_TYPES.map((rt) => (
                <SelectItem key={rt.value} value={rt.value}>
                  {rt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date range */}
          <div className="flex items-center gap-1">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
            <Input
              type="date"
              value={search.dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              className="h-8 w-36"
            />
            <Label className="text-xs text-muted-foreground whitespace-nowrap">To</Label>
            <Input
              type="date"
              value={search.dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              className="h-8 w-36"
            />
          </div>

          {/* Clear all filters */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={clearAllFilters}>
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}

          <div className="ml-auto flex items-center gap-2">
            <DataTableViewOptions table={table} />

            {/* Export — every row matching the current filters, not just this page */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              onClick={handleExportToExcel}
              disabled={isExporting}
            >
              <Download className="h-3 w-3" />
              {isExporting ? 'Exporting…' : 'Export to Excel'}
            </Button>
          </div>
        </div>

        {/* Active filter chips */}
        {(selectedRoles.length > 0 || selectedActions.length > 0) && (
          <div className="flex flex-wrap gap-1">
            {selectedRoles.map((role) => (
              <Badge
                key={role}
                variant="secondary"
                className="cursor-pointer gap-1 pr-1 text-xs"
                onClick={() => handleRoleToggle(role)}
              >
                {ALL_ROLES.find((r) => r.value === role)?.label ?? role}
                <X className="h-2.5 w-2.5" />
              </Badge>
            ))}
            {selectedActions.map((action) => (
              <Badge
                key={action}
                variant={
                  ACTION_BADGE_VARIANT[action as keyof typeof ACTION_BADGE_VARIANT] ?? 'outline'
                }
                className="cursor-pointer gap-1 pr-1 text-xs"
                onClick={() => handleActionToggle(action)}
              >
                {action}
                <X className="h-2.5 w-2.5" />
              </Badge>
            ))}
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className={COLUMN_HEADER_CLASS[header.column.id]}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {table.getAllLeafColumns().map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.original.auditLogId}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length}
                    className="h-24 text-center"
                  >
                    No audit logs found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-muted-foreground">
            {pagination ? (
              <>
                {pagination.totalCount} total record
                {pagination.totalCount !== 1 ? 's' : ''}
              </>
            ) : null}
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <select
                className="h-8 w-[70px] rounded border border-input bg-background px-2 text-sm"
                value={pageSize}
                onChange={(e) => goToPageSize(Number(e.target.value))}
              >
                {[20, 50, 100].map((ps) => (
                  <option key={ps} value={ps}>
                    {ps}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex w-[120px] items-center justify-center text-sm font-medium">
              Page {page} of {pagination?.totalPages ?? 1}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => goToPage(1)}
                disabled={!pagination?.hasPreviousPage}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => goToPage(page - 1)}
                disabled={!pagination?.hasPreviousPage}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => goToPage(page + 1)}
                disabled={!pagination?.hasNextPage}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => goToPage(pagination?.totalPages ?? 1)}
                disabled={!pagination?.hasNextPage}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  )
}
